// src/components/NavigationItem.tsx

import React, { useState, useEffect } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    alpha,
    Box,
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    useTheme,
    Tooltip,
} from '@mui/material';
import { GoChevronRight } from 'react-icons/go';

import Icon from './Icon'; // Assuming Icon.tsx is in the same directory
import type { Navigation } from '@/types/navigation';

interface NavigationItemProps {
    item: Navigation;
    open: boolean; // Is the main drawer open?
}

export const NavigationItem: React.FC<NavigationItemProps> = ({ item, open }) => {
    const theme = useTheme();
    const location = useLocation();

    // Determine if the current item or one of its children is active
    const hasActiveChild = item.subNavigation?.some(
        (child) => child.link === '/' ? location.pathname === '/' : location.pathname.startsWith(child.link)
    ) ?? false;

    const isCurrentPathActive = item.link 
        ? (item.link === '/' ? location.pathname === '/' : location.pathname.startsWith(item.link)) 
        : false;

    const isActive = isCurrentPathActive || hasActiveChild;

    const [isCollapsed, setIsCollapsed] = useState(isActive); // Expand if active on initial render

    const hasSubNavigation = item.subNavigation && item.subNavigation.length > 0;

    // If the active path changes, check if we need to expand this item
    useEffect(() => {
        if (hasActiveChild) {
            setIsCollapsed(true);
        }
    }, [location.pathname, hasActiveChild]);

    const handleToggleCollapse = () => {
        if (hasSubNavigation) {
            setIsCollapsed((prev) => !prev);
        }
    };

    const buttonContent = (
        <ListItemButton
            component={hasSubNavigation ? 'div' : RouterLink}
            to={!hasSubNavigation ? item.link : undefined}
            onClick={handleToggleCollapse}
            sx={{
                mx: open ? 1.5 : 1,
                borderRadius: '10px',
                justifyContent: open ? 'flex-start' : 'center',
                mb: 0.5,
                py: 1,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                ...(isActive && !hasSubNavigation && {
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -12,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        height: '60%',
                        width: '4px',
                        backgroundColor: theme.palette.primary.main,
                        borderRadius: '0 4px 4px 0',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    }
                }),
                backgroundColor: isActive && !hasSubNavigation
                    ? alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.1 : 0.15)
                    : 'transparent',
                color: isActive && !hasSubNavigation
                    ? theme.palette.primary.main
                    : (theme.palette.mode === 'light'
                        ? alpha(theme.palette.text.primary, 0.75)
                        : alpha(theme.palette.text.primary, 0.6)),
                '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.04 : 0.08),
                    color: theme.palette.primary.main,
                    transform: open ? 'translateX(4px)' : 'none',
                    '& .MuiListItemIcon-root': {
                        color: theme.palette.primary.main,
                    }
                },
                '& .MuiListItemIcon-root': {
                    transition: 'color 0.2s',
                    color: isActive && !hasSubNavigation ? theme.palette.primary.main : 'inherit',
                }
            }}
        >
            <ListItemIcon sx={{ minWidth: 0, mr: open ? 1.5 : 0, justifyContent: 'center', color: 'inherit' }}>
                <Icon icon={item.image} />
            </ListItemIcon>

            {open && (
                <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                        fontSize: '12px',
                        fontWeight: 500,
                        textTransform: 'capitalize',
                    }}
                />
            )}

            {open && hasSubNavigation && (
                <Box
                    sx={{
                        transition: 'transform 0.2s',
                        transform: isCollapsed ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}
                >
                    <GoChevronRight />
                </Box>
            )}
        </ListItemButton>
    );

    return (
        <>
            {!open ? (
                <Tooltip title={item.label} placement="right" arrow>
                    {buttonContent}
                </Tooltip>
            ) : (
                buttonContent
            )}

            {open && hasSubNavigation && (
                <Collapse in={isCollapsed} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding sx={{ pl: 2.5 }}>
                        {item?.subNavigation?.map((child) => (
                            <NavigationItem key={child.id} item={child} open={open} />
                        ))}
                    </List>
                </Collapse>
            )}
        </>
    );
};